import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Inbox,
  LoaderCircle,
  RefreshCw,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

export function StatusBadge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`status-badge status-badge--${tone}`}>{children}</span>;
}

export function Button({
  variant = "primary",
  icon: Icon,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
  icon?: LucideIcon;
}) {
  return (
    <button className={`button button--${variant} ${className}`} {...props}>
      {Icon ? <Icon size={18} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  prefixIcon?: LucideIcon;
}

export function Field({ label, hint, error, prefixIcon: Icon, id, className = "", ...props }: FieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const descriptionId = `${fieldId}-description`;
  return (
    <label className={`field ${className}`} htmlFor={fieldId}>
      <span className="field__label">{label}</span>
      <span className={`field__control${error ? " field__control--error" : ""}`}>
        {Icon ? <Icon size={18} aria-hidden="true" /> : null}
        <input id={fieldId} aria-invalid={Boolean(error)} aria-describedby={hint || error ? descriptionId : undefined} {...props} />
      </span>
      {hint || error ? (
        <span id={descriptionId} className={`field__hint${error ? " field__hint--error" : ""}`}>
          {error ?? hint}
        </span>
      ) : null}
    </label>
  );
}

export function SelectField({
  label,
  children,
  id,
  className = "",
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <label className={`field ${className}`} htmlFor={fieldId}>
      <span className="field__label">{label}</span>
      <span className="select-control">
        <select id={fieldId} {...props}>{children}</select>
        <ChevronDown size={17} aria-hidden="true" />
      </span>
    </label>
  );
}

export function SearchField({ value, onChange, placeholder = "Buscar...", label = "Buscar" }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}) {
  return (
    <label className="search-field">
      <span className="sr-only">{label}</span>
      <Search size={18} aria-hidden="true" />
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} type="search" />
      {value ? (
        <button type="button" onClick={() => onChange("")} aria-label="Limpiar búsqueda">
          <X size={16} />
        </button>
      ) : null}
    </label>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function SectionHeader({ title, count, action }: { title: string; count?: number; action?: ReactNode }) {
  return (
    <div className="section-header">
      <h2>
        {title}
        {typeof count === "number" ? <span className="section-header__count">{count}</span> : null}
      </h2>
      {action}
    </div>
  );
}

export function StatePanel({
  state,
  title,
  description,
  onRetry,
}: {
  state: "loading" | "empty" | "error" | "forbidden";
  title?: string | undefined;
  description?: string | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const config = {
    loading: { icon: LoaderCircle, title: "Cargando información", description: "Estamos actualizando los datos operativos." },
    empty: { icon: Inbox, title: "No hay registros", description: "No encontramos resultados con los filtros actuales." },
    error: { icon: AlertCircle, title: "No pudimos cargar los datos", description: "Revisa tu conexión y vuelve a intentarlo." },
    forbidden: { icon: AlertCircle, title: "Acceso restringido", description: "Tu perfil no tiene permiso para consultar esta sección." },
  }[state];
  const Icon = config.icon;
  return (
    <div className={`state-panel state-panel--${state}`} role={state === "error" ? "alert" : "status"} aria-live="polite">
      <Icon className={state === "loading" ? "spin" : ""} aria-hidden="true" />
      <h2>{title ?? config.title}</h2>
      <p>{description ?? config.description}</p>
      {onRetry ? <Button variant="secondary" icon={RefreshCw} onClick={onRetry}>Reintentar</Button> : null}
    </div>
  );
}

export function InlineNotice({ tone = "info", children }: { tone?: Exclude<Tone, "neutral">; children: ReactNode }) {
  const Icon = tone === "success" ? CheckCircle2 : AlertCircle;
  return (
    <div className={`inline-notice inline-notice--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon size={19} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

interface DialogContextValue {
  titleId: string;
  close: () => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export type DialogSize = "compact" | "medium" | "executive";

/** Open dialogs, oldest first: only the top one answers Escape and keeps Tab inside. */
const dialogStack: symbol[] = [];

export function Dialog({ open, size = "medium", className = "", title, eyebrow, footer, initialFocusRef, onClose, children }: {
  open: boolean;
  size?: DialogSize;
  /** Extra class for a dialog that needs its own size, e.g. the quote editor. */
  className?: string;
  title: string;
  eyebrow?: string;
  footer?: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    (initialFocusRef?.current ?? closeRef.current)?.focus();
    const token = Symbol("dialog");
    dialogStack.push(token);
    const onKeyDown = (event: KeyboardEvent) => {
      // A dialog opened from another one (e.g. an action inside the case file) is the only one that reacts.
      if (dialogStack[dialogStack.length - 1] !== token) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      ) ?? []).filter((element) => element.getAttribute("aria-hidden") !== "true");
      if (!focusable.length) {
        event.preventDefault();
        closeRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("dialog-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      dialogStack.splice(dialogStack.indexOf(token), 1);
      if (!dialogStack.length) document.body.classList.remove("dialog-open");
      previous?.focus();
    };
  }, [initialFocusRef, open]);

  if (!open) return null;
  // Portal: a dialog opened from a container-query panel must not be positioned inside that panel.
  return createPortal(
    <div className="dialog-layer">
      <button className="dialog-backdrop" type="button" aria-label={`Cerrar ventana de ${title}`} onClick={onClose} />
      <section ref={dialogRef} className={`dialog dialog--${size}${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="dialog__header">
          <div>
            {eyebrow ? <span>{eyebrow}</span> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button ref={closeRef} className="icon-button" type="button" onClick={onClose} aria-label="Cerrar ventana">
            <X />
          </button>
        </header>
        <DialogContext.Provider value={{ titleId, close: onClose }}>
          <div className="dialog__body">{children}</div>
          {footer ? <footer className="dialog__footer">{footer}</footer> : null}
        </DialogContext.Provider>
      </section>
    </div>,
    document.body,
  );
}

export function useDialog(): DialogContextValue {
  const value = useContext(DialogContext);
  if (!value) throw new Error("useDialog debe usarse dentro de Dialog.");
  return value;
}

export function LinkAction({ children }: { children: ReactNode }) {
  return (
    <span className="link-action">
      {children}
      <ArrowRight size={15} aria-hidden="true" />
    </span>
  );
}
